import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Play, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { api } from '@/utils/api';
import { useLLMProfiles } from '@/hooks/useSettings';
import type { LLMProfile } from '@/types';

interface ProcessingStatus {
  total_runs: number;
  recent_runs: Array<{
    run_id: string;
    status: string;
    t0: string;
    t1: string;
    tweets_processed: number;
    tweets_classified: number;
    is_manual: boolean;
  }>;
  pending_items: {
    tweets: number;
    rss_articles: number;
    total: number;
  };
  last_run: string | null;
}

interface UnprocessedItem {
  id: number;
  type: string;
  content: string;
  author?: string;
  source?: string;
  created_at: string;
  reason: string;
}

interface UnprocessedDetails {
  tweets: UnprocessedItem[];
  rss_articles: UnprocessedItem[];
  total: number;
}

interface LLMTestResult {
  profile_id: number;
  name: string;
  model: string;
  status: string;
  message: string;
  latency_ms?: number;
}

interface SingleProcessResult {
  success: boolean;
  item_type: string;
  item_id: number;
  message: string;
  category?: string;
  confidence?: number;
}

export function DebugProcessingPanel() {
  const [selectedProfile, setSelectedProfile] = useState<number | null>(null);
  
  const { data: profiles } = useLLMProfiles();
  
  const { data: status, isLoading: isLoadingStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['debug-processing-status'],
    queryFn: async () => {
      const response = await api.get('/debug/processing/status');
      return response.data as ProcessingStatus;
    },
  });
  
  const { data: unprocessed, isLoading: isLoadingUnprocessed, refetch: refetchUnprocessed } = useQuery({
    queryKey: ['debug-unprocessed-details'],
    queryFn: async () => {
      const response = await api.get('/debug/processing/unprocessed-details?limit=10');
      return response.data as UnprocessedDetails;
    },
  });
  
  const testLLMMutation = useMutation({
    mutationFn: async (profileId: number) => {
      const response = await api.post('/debug/processing/test-llm-profile', { profile_id: profileId });
      return response.data as LLMTestResult;
    },
  });
  
  const processSingleMutation = useMutation({
    mutationFn: async ({ item_type, item_id }: { item_type: string; item_id: number }) => {
      const response = await api.post('/debug/processing/process-single', { item_type, item_id });
      return response.data as SingleProcessResult;
    },
  });
  
  const refreshAll = () => {
    refetchStatus();
    refetchUnprocessed();
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">LLM 处理调试面板</h2>
        <Button variant="outline" size="sm" onClick={refreshAll}>
          <RefreshCw className="h-4 w-4 mr-2" />
          刷新
        </Button>
      </div>
      
      {/* Processing Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">处理状态</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingStatus ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : status ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-muted rounded-lg text-center">
                  <div className="text-2xl font-bold">{status.total_runs}</div>
                  <div className="text-sm text-muted-foreground">总运行次数</div>
                </div>
                <div className="p-3 bg-muted rounded-lg text-center">
                  <div className="text-2xl font-bold">{status.pending_items.total}</div>
                  <div className="text-sm text-muted-foreground">待处理项目</div>
                </div>
                <div className="p-3 bg-muted rounded-lg text-center">
                  <div className="text-2xl font-bold">
                    {status.last_run ? new Date(status.last_run).toLocaleDateString() : '无'}
                  </div>
                  <div className="text-sm text-muted-foreground">最后运行</div>
                </div>
              </div>
              
              {status.recent_runs.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">最近运行</h4>
                  <div className="space-y-2">
                    {status.recent_runs.slice(0, 3).map((run) => (
                      <div key={run.run_id} className="flex items-center gap-3 p-2 bg-muted rounded text-sm">
                        {run.status === 'success' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : run.status === 'failed' ? (
                          <XCircle className="h-4 w-4 text-red-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                        )}
                        <span className="font-mono">{run.run_id.slice(0, 16)}...</span>
                        <Badge variant={run.is_manual ? "default" : "secondary"} className="text-xs">
                          {run.is_manual ? '手动' : '自动'}
                        </Badge>
                        <span className="text-muted-foreground">
                          {run.tweets_processed} 推文 / {run.tweets_classified} 分类
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
      
      {/* LLM Profile Testing */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">LLM 配置测试</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {profiles?.map((profile: LLMProfile) => (
                <Button
                  key={profile.id}
                  variant={selectedProfile === profile.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSelectedProfile(profile.id);
                    testLLMMutation.mutate(profile.id);
                  }}
                  disabled={testLLMMutation.isPending}
                >
                  {testLLMMutation.isPending && selectedProfile === profile.id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  {profile.name}
                </Button>
              ))}
            </div>
            
            {testLLMMutation.data && (
              <div className={`p-3 rounded-lg ${testLLMMutation.data.status === 'ok' ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-2">
                  {testLLMMutation.data.status === 'ok' ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="font-medium">{testLLMMutation.data.name}</span>
                  <Badge variant="outline" className="text-xs">{testLLMMutation.data.model}</Badge>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {testLLMMutation.data.message}
                </div>
                {testLLMMutation.data.latency_ms && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    延迟: {testLLMMutation.data.latency_ms}ms
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Unprocessed Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">待处理项目 ({unprocessed?.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingUnprocessed ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : unprocessed ? (
            <div className="space-y-4">
              {/* Tweets */}
              {unprocessed.tweets.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">推文 ({unprocessed.tweets.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unprocessed.tweets.map((tweet) => (
                      <div key={tweet.id} className="p-3 bg-muted rounded text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">@{tweet.author}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2"
                            onClick={() => processSingleMutation.mutate({ item_type: 'tweet', item_id: tweet.id })}
                            disabled={processSingleMutation.isPending}
                          >
                            {processSingleMutation.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                        <p className="text-muted-foreground line-clamp-2">{tweet.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* RSS Articles */}
              {unprocessed.rss_articles.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">RSS 文章 ({unprocessed.rss_articles.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unprocessed.rss_articles.map((article) => (
                      <div key={article.id} className="p-3 bg-muted rounded text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{article.source}</span>
                          <Badge variant="outline" className="text-xs">{article.reason}</Badge>
                        </div>
                        <p className="text-muted-foreground line-clamp-2">{article.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {unprocessed.total === 0 && (
                <div className="text-center text-muted-foreground py-4">
                  所有项目已处理完成
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
      
      {/* Single Process Result */}
      {processSingleMutation.data && (
        <Card className={processSingleMutation.data.success ? 'border-green-500' : 'border-red-500'}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              {processSingleMutation.data.success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="font-medium">
                {processSingleMutation.data.item_type} #{processSingleMutation.data.item_id}
              </span>
            </div>
            <div className="mt-1 text-sm">{processSingleMutation.data.message}</div>
            {processSingleMutation.data.category && (
              <div className="mt-2 flex items-center gap-2">
                <Badge>{processSingleMutation.data.category}</Badge>
                <span className="text-sm text-muted-foreground">
                  置信度: {(processSingleMutation.data.confidence! * 100).toFixed(1)}%
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
